import React from 'react';
import PropTypes from 'prop-types';

import Checkbox from '@decisiv/ui-components/lib/components/Checkbox';
import Tooltip from '@decisiv/ui-components/lib/components/Tooltip';

import { LockedControl } from './styles';

/**
 * A matrix checkbox, carrying the reason it is disabled when there is one.
 *
 * A disabled checkbox cannot carry its own tooltip: KeyDS Tooltip binds native
 * hover and focus events to its target, and a disabled input fires none of them
 * and is not focusable. So the tooltip goes on a wrapper, with the input inside.
 *
 * Note jsdom *does* dispatch mouse events on disabled elements, so a test firing
 * on the input rather than the wrapper passes here and fails in a browser. Tests
 * target the wrapper by its accessible name.
 *
 * Plain KeyDS Checkbox, not the wizard's WizardCheckbox — that workaround is for
 * controlled inputs inside a React 16 portal, and the tab body is not one.
 */
function GuardedCheckbox({ checked, disabled, label, lockReason, onChange }) {
  const control = (
    <Checkbox
      aria-label={label}
      checked={checked}
      disabled={disabled}
      name={label}
      onChange={() => onChange(!checked)}
    />
  );

  if (!lockReason) {
    return control;
  }

  return (
    <Tooltip
      placement="top"
      target={
        <LockedControl aria-label={`${label} — ${lockReason}`} tabIndex={0}>
          {control}
        </LockedControl>
      }
    >
      {lockReason}
    </Tooltip>
  );
}

GuardedCheckbox.propTypes = {
  checked: PropTypes.bool,
  disabled: PropTypes.bool,
  /** Accessible name — "Fleet Admin for Amazon Logistics". */
  label: PropTypes.string.isRequired,
  /** Why the control is disabled. Absent for a read-only page, where a reason
   *  on every control would be noise. */
  lockReason: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

GuardedCheckbox.defaultProps = {
  checked: false,
  disabled: false,
  lockReason: null,
};

export default GuardedCheckbox;
